import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Auth helper functions
export const signInWithEmail = async (email: string) => {
  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`
    }
  })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

// Video transcript functions
export const getVideoTranscript = async (videoId: string) => {
  // Clean video_id to remove any query parameters
  const cleanVideoId = videoId.split('&')[0]
  
  const { data, error } = await supabase
    .from('video_transcripts')
    .select('*')
    .eq('video_id', cleanVideoId)
    .single()

  if (data) {
    // Convert the transcript string back to array format if it contains timestamps
    const transcriptLines = data.transcript.split('\n')
    if (transcriptLines[0].match(/^\[\d{2}:\d{2}(:\d{2})?\]/)) {
      data.transcript = transcriptLines.map((line: string) => {
        const match = line.match(/^\[(\d{2}:\d{2}(?::\d{2})?)\]\s*(.*)$/)
        if (match) {
          return {
            timestamp: match[1],
            text: match[2]
          }
        }
        return line
      })
    }
  }

  return { data, error }
}

export const saveVideoTranscript = async (videoData: {
  video_id: string
  video_title: string
  video_url: string
  transcript: string | Array<{ text: string; timestamp: string }>
  channel_name?: string
  thumbnail_url?: string
}) => {
  try {
    // Format transcript if it's an array
    const formattedTranscript = Array.isArray(videoData.transcript)
      ? videoData.transcript
          .map(item => `[${item.timestamp}] ${item.text}`)
          .join('\n')
      : videoData.transcript;

    // Clean video_id to remove any query parameters
    const cleanVideoId = videoData.video_id.split('&')[0];

    // Prepare data for saving
    const dataToSave = {
      video_id: cleanVideoId,
      video_title: videoData.video_title,
      video_url: videoData.video_url,
      transcript: formattedTranscript,
      channel_name: videoData.channel_name,
      thumbnail_url: videoData.thumbnail_url
    };

    // First try to get existing transcript
    const { data: existingTranscript } = await supabase
      .from('video_transcripts')
      .select('id')
      .eq('video_id', cleanVideoId)
      .single();

    if (existingTranscript) {
      // Update existing transcript
      const { data, error } = await supabase
        .from('video_transcripts')
        .update(dataToSave)
        .eq('video_id', cleanVideoId)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating transcript:', error);
        throw error;
      }
      return { data, error: null };
    } else {
      // Insert new transcript
      const { data, error } = await supabase
        .from('video_transcripts')
        .insert(dataToSave)
        .select()
        .single();
      
      if (error) {
        console.error('Error inserting transcript:', error);
        throw error;
      }
      return { data, error: null };
    }
  } catch (error) {
    console.error('Error saving video transcript:', error);
    return { data: null, error };
  }
}

// Watch history functions
export const saveToWatchHistory = async (videoId: string) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { error: new Error('User not authenticated') }

  // Clean video_id to remove any query parameters
  const cleanVideoId = videoId.split('&')[0]

  const { data, error } = await supabase
    .from('watch_history')
    .insert({
      user_id: user.id,
      video_id: cleanVideoId
    })
    .select()
    .single()
  return { data, error }
}

export const getRecentWatchHistory = async (limit = 10) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { error: new Error('User not authenticated') }

  const { data, error } = await supabase
    .from('watch_history')
    .select(`
      *,
      video_transcripts (
        video_id,
        video_title,
        thumbnail_url,
        channel_name
      )
    `)
    .eq('user_id', user.id)
    .order('watched_at', { ascending: false })
    .limit(limit)
  return { data, error }
} 